import React, { useState } from 'react';
import { ArrowLeft, TrendingUp, Building2, DollarSign, Info } from 'lucide-react';
import { BrokerConnectionModal } from './BrokerConnectionModal';
import { Button } from '../ui/Button';
import TaxDepreciationDemo from '../demo/TaxDepreciationDemo';
import LoanCalculatorDemo from '../demo/LoanCalculatorDemo';

interface ValuationBreakdownType {
  minTotal: number;
  maxTotal: number;
  landValue: number;
  buildingSiteValue: number;
  fixturesValue: number;
  ffeValue: number;
  minBusinessValue: number;
  maxBusinessValue: number;
}

interface DemoValuationResultProps {
  data: any;
  valuationBreakdown: ValuationBreakdownType;
  onBack: () => void;
}

export default function DemoValuationResult({ 
  data, 
  valuationBreakdown,
  onBack 
}: DemoValuationResultProps): React.ReactNode {
  const [showBrokerModal, setShowBrokerModal] = useState(false);
  
  // Format currency to millions with 2 decimal places
  const toMillions = (num: number) => {
    return (num / 1000000).toFixed(2);
  };
  
  // Format currency to K for thousands
  const toThousands = (num: number) => {
    return (num / 1000).toFixed(0);
  };

  // Format currency with commas
  const formatCurrency = (num: number) => {
    return num.toLocaleString('en-US');
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Calculator
      </button>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
  {/* Cost to Compete - Orange Theme */}
  <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-center">
    <h3 className="text-lg font-semibold text-orange-800 mb-2">Cost to Compete</h3>
    <p className="text-3xl font-bold text-orange-600 mb-2">
      ${toMillions(valuationBreakdown.minTotal * 0.85)}-{toMillions(valuationBreakdown.maxTotal * 0.90)}M
    </p>
    <p className="text-sm text-orange-700">
      Replacement cost for competitive facility (85-90% of estimated value)
    </p>
  </div>

  {/* Estimated Property Value - Blue Theme */}
  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center">
    <h3 className="text-lg font-semibold text-blue-800 mb-2">Estimated Property Value</h3>
    <p className="text-3xl font-bold text-blue-600 mb-2">
      ${toMillions(valuationBreakdown.minTotal)}-{toMillions(valuationBreakdown.maxTotal)}M
    </p>
    <p className="text-sm text-blue-700">
      Current market value assessment
    </p>
  </div>

  {/* Entrepreneurial Value - Green Theme */}
  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
    <h3 className="text-lg font-semibold text-green-800 mb-2">Entrepreneurial Value</h3>
    <p className="text-3xl font-bold text-green-600 mb-2">
      ${toMillions(valuationBreakdown.minTotal * 1.15)}-{toMillions(valuationBreakdown.maxTotal * 1.25)}M
    </p>
    <p className="text-sm text-green-700">
      Maximum value with optimal operations (115-125% of estimated value)
    </p>
  </div>
</div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          Value Breakdown 
          <span className="ml-2 text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-md">Detailed Analysis</span>
        </h3>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <div className="space-y-4">
            <div className="flex pb-2 border-b border-gray-200">
              <span className="font-medium flex-1">Component</span>
              <span className="font-medium w-48 text-right">Estimated Value</span>
              <span className="font-medium w-40 text-right">Confidence Score</span>
            </div>
            
            <div className="flex items-center py-1">
              <span className="flex items-center flex-1 min-w-0">
                <span className="w-3 h-3 bg-blue-700 rounded-sm inline-block mr-2"></span>
                Underlying Land Value
              </span>
              <span className="font-medium w-48 text-right">${formatCurrency(valuationBreakdown.landValue)}</span>
              <span className="text-blue-800 font-semibold w-40 text-right">7.7</span>
            </div>
            
            <div className="flex items-center py-1">
  <span className="flex items-center flex-1 min-w-0">
     <span className="w-3 h-3 bg-blue-800 rounded-sm inline-block mr-2"></span>
    Convenience Store Building
  </span>
  <span className="font-medium w-48 text-right">${formatCurrency(Math.round(valuationBreakdown.buildingSiteValue * 0.65))}</span>
  <span className="text-blue-800 font-semibold w-40 text-right">8.4</span>
</div>

<div className="flex items-center py-1">
  <span className="flex items-center flex-1 min-w-0">
    <span className="w-3 h-3 bg-blue-700 rounded-sm inline-block mr-2"></span>
    Car Wash Building
  </span>
  <span className="font-medium w-48 text-right">${formatCurrency(Math.round(valuationBreakdown.buildingSiteValue * 0.15))}</span>
  <span className="text-blue-800 font-semibold w-40 text-right">8.0</span>
</div>

<div className="flex items-center py-1">
  <span className="flex items-center flex-1 min-w-0">
  <span className="w-3 h-3 bg-blue-600 rounded-sm inline-block mr-2"></span>
    Car Wash Equipment
  </span>
  <span className="font-medium w-48 text-right">${formatCurrency(Math.round(valuationBreakdown.fixturesValue * 0.4))}</span>
  <span className="text-blue-800 font-semibold w-40 text-right">8.7</span>
</div>

<div className="flex items-center py-1">
  <span className="flex items-center flex-1 min-w-0">
  <span className="w-3 h-3 bg-blue-500 rounded-sm inline-block mr-2"></span>
    Site Improvements (Paving, Lighting)
  </span>
  <span className="font-medium w-48 text-right">${formatCurrency(Math.round(valuationBreakdown.buildingSiteValue * 0.20))}</span>
  <span className="text-blue-800 font-semibold w-40 text-right">7.9</span>
</div>
            
            <div className="flex items-center py-1">
              <span className="flex items-center flex-1 min-w-0">
                <span className="w-3 h-3 bg-blue-400 rounded-sm inline-block mr-2"></span>
                Permanent Fixtures (Fueling Equipment)
              </span>
              <span className="font-medium w-48 text-right">${formatCurrency(valuationBreakdown.fixturesValue * 0.6)}</span>
              <span className="text-blue-800 font-semibold w-40 text-right">9.1</span>
            </div>
            
            <div className="flex items-center py-1">
              <span className="flex items-center flex-1 min-w-0">
                <span className="w-3 h-3 bg-blue-300 rounded-sm inline-block mr-2"></span>
                FF&E Value
              </span>
              <span className="font-medium w-48 text-right">${formatCurrency(valuationBreakdown.ffeValue)}</span>
              <span className="text-blue-800 font-semibold w-40 text-right">8.8</span>
            </div>
            
            <div className="flex items-center py-1">
              <span className="flex items-center flex-1 min-w-0">
                <span className="w-3 h-3 bg-blue-200 rounded-sm inline-block mr-2"></span>
                Business Value
              </span>
              <span className="font-medium w-48 text-right">
                ${formatCurrency(valuationBreakdown.minBusinessValue)} - ${formatCurrency(valuationBreakdown.maxBusinessValue)}
              </span>
              <span className="text-blue-800 font-semibold w-40 text-right">8.0</span>
            </div>
            
            <div className="pt-3 mt-3 border-t border-gray-300 flex items-center font-bold">
              <span className="flex-1">Total Going-Concern Value</span>
              <span className="text-blue-600 w-48 text-right">
                ${formatCurrency(valuationBreakdown.minTotal)} - ${formatCurrency(valuationBreakdown.maxTotal)}
              </span>
              <span className="text-blue-800 font-semibold w-40 text-right">9.0</span>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
          <div className="flex">
            <Info className="w-5 h-5 mr-2 flex-shrink-0" />
            <div>
              <p>The business value component can vary significantly based on operational efficiency, 
              customer loyalty, brand strength, and future growth potential.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {[
          {
            label: 'Monthly Fuel Revenue',
            value: `$${(Number(data.monthlyGallons) * Number(data.fuelMargin) / 1000).toFixed(0)}K`,
            icon: <TrendingUp className="w-6 h-6" />
          },
          {
            label: 'Store Revenue',
            value: `$${(Number(data.storeRevenue) / 1000).toFixed(0)}K`,
            icon: <Building2 className="w-6 h-6" />
          },
          {
            label: 'Property Size',
            value: `${Number(data.propertySize).toLocaleString()} sq ft`,
            icon: <DollarSign className="w-6 h-6" />
          }
        ].map((metric) => (
          <div
            key={metric.label}
            className="bg-gray-50 p-4 rounded-lg"
          >
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              {metric.icon}
              <span className="font-semibold">{metric.label}</span>
            </div>
            <p className="text-2xl font-bold">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Valuation Insights</h3>
        <p className="text-gray-600">
          Based on your property's characteristics and current market conditions, 
          here are the key factors influencing your valuation:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600">
          <li>Prime {data.location} location with high traffic potential</li>
          <li>{data.numberOfPumps} fuel pumps providing excellent service capacity</li>
          <li>{data.hasCarWash ? 'Additional revenue from car wash services' : 'Potential for car wash addition'}</li>
          <li>{Number(data.yearBuilt) > 2000 ? 'Modern facilities' : 'Potential for facility upgrades'}</li>
          <li>Strong {data.brandName} branded identity with {data.storeBrand} store</li>
        </ul>
      </div>

      {/* --- TAX DEPRECIATION --- */}
      <div className="mt-12 border-t pt-8">
        <TaxDepreciationDemo totalPropertyValue={valuationBreakdown.maxTotal} />
      </div>
      
      {/* --- LOAN CALCULATOR --- */}
      <div className="mt-12 border-t pt-8">
        <LoanCalculatorDemo totalPropertyValue={valuationBreakdown.maxTotal} />
      </div>

      <div className="mt-8 border-t pt-6">
        <h3 className="text-xl font-semibold mb-4">Next Steps</h3>
        <p className="text-gray-600 mb-6">
          Ready to explore your options? Connect with a trusted local broker specializing in gas station and convenience store properties.
        </p>
        <div className="flex gap-4">
          <Button
            id="connect-broker-button"
            onClick={() => console.log('Demo broker connection button clicked')}
            className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-2 rounded-lg"
          >
            Connect with a Local Broker
          </Button>
          <Button
            id="connect-lender-button"
            onClick={() => console.log('Demo lender connection button clicked')}
            className="bg-green-600 text-white hover:bg-green-700 px-6 py-2 rounded-lg"
          >
            Connect with a Local Lender
          </Button>
        </div>
      </div>

      <BrokerConnectionModal
        isOpen={showBrokerModal}
        onClose={() => setShowBrokerModal(false)}
      />
    </div>
  );
};